import { LocalDate, toLocalDate } from '@spa-frontend/date-lib';
import { sortByFn } from 'essential-utils';
import { observable } from 'mobx';
import React from 'react';
import { component, inject } from 'tsdi';
import { wrapRequest } from 'wrap-request';

import { Api } from '../core/api';
import {
    ConnectApiBasicStudioDataDtoV2,
    GetActiveContractsRequestDto
} from '../core/api/dtos';
import { featureEnabled } from '../core/common/feature';
import { Field, Form, WrappedField } from '../core/common/forms';
import { injectTSDI } from '../core/common/tsdi';
import { required } from '../core/common/validation';
import { I18n } from '../core/i18n';
import { UrlStore } from '../core/url-store';

type ContractDissolutionPurpose = 'CANCELLATION' | 'WITHDRAWAL';

@component
export class SharedContractDissolutionStore {
    @inject
    private api!: Api;

    @inject
    private urlStore!: UrlStore;

    @observable
    public studios?: ConnectApiBasicStudioDataDtoV2[];

    @observable
    private loadedPurpose?: ContractDissolutionPurpose;

    public readonly getStudiosRequest = wrapRequest(
        (purpose: ContractDissolutionPurpose) =>
            this.api.client.ConnectApiContractControllerV2.getActiveStudios({
                purpose
            })
    );

    public getStudioInfo = wrapRequest(async (studioId: number) =>
        this.api.client.ConnectApiContractController.getStudioInfo(studioId)
    );

    public getLegalLinks = wrapRequest((studioId: number) =>
        this.api.client.StudioControllerV2.getStudioLegalLinks(studioId)
    );

    public getStudioDetails = wrapRequest((studioId: number) =>
        this.api.client.StudioControllerV2.getStudioDetails(studioId)
    );

    public get selectedStudioCountry(): string | undefined {
        return this.getStudioDetails.$?.address?.countryCodeAlpha2;
    }

    public loadStudioInfo = async (studioId: number) => {
        const requests: Promise<unknown>[] = [
            this.getStudioInfo.request(studioId)
        ];
        if (featureEnabled('HIDE_IMPRINT_FOR_NON_EU')) {
            requests.push(this.getStudioDetails.request(studioId));
        }
        await Promise.all(requests);
    };

    public readonly memberDataForm = new Form<GetActiveContractsRequestDto>({
        firstname: new Field<string>('', required()),
        lastname: new Field<string>('', required()),
        dateOfBirth: new Field<LocalDate>(
            LocalDate.now().subDays(1),
            required()
        ),
        customerNumber: new Field<string | undefined>(undefined)
    });

    public loadStudios = async (purpose: ContractDissolutionPurpose) => {
        if (this.studios && this.loadedPurpose === purpose) {
            return;
        }
        this.getStudiosRequest.reset(undefined);
        await this.getStudiosRequest.request(purpose);
        const result = await this.getStudiosRequest.when();
        this.studios = result
            ? [...result].sort(
                  sortByFn((studio) => studio.studioPublicDto?.name)
              )
            : undefined;
        this.loadedPurpose = result ? purpose : undefined;
    };

    public prefillSelectedStudio = async (
        studioIdField: WrappedField<number | undefined>,
        purpose: ContractDissolutionPurpose
    ) => {
        await this.loadStudios(purpose);
        const selectedStudioId =
            (this.urlStore.studio && parseInt(this.urlStore.studio, 10)) ||
            undefined;
        if (
            selectedStudioId &&
            this.studios?.some((studio) => studio.id === selectedStudioId)
        ) {
            studioIdField.reset(selectedStudioId);
            await this.loadStudioInfo(selectedStudioId);
        } else if (
            this.getStudiosRequest.result?.length === 1 &&
            this.studios?.length === 1
        ) {
            studioIdField.reset(this.studios[0].id);
            await this.loadStudioInfo(this.studios[0].id);
        }
    };

    public getStudio = (
        studioId: number | undefined
    ): ConnectApiBasicStudioDataDtoV2 | undefined => {
        return this.studios?.find((studio) => studio.id === studioId);
    };

    public updateDateIfValid = (
        event: React.ChangeEvent<{ name?: string; value: unknown }>,
        dateField: WrappedField<LocalDate | undefined>
    ) => {
        const localDate =
            typeof event.target.value === 'string'
                ? toLocalDate(event.target.value)
                : undefined;
        if (
            dateField &&
            localDate &&
            localDate.isValid() &&
            !localDate.isSame(LocalDate.now())
        ) {
            dateField.reset(localDate);
        }
    };

    public updateCancellationType = (
        event: React.ChangeEvent<{ name?: string; value: unknown }>,
        cancellationType: WrappedField<string>,
        cancellationDateType?: WrappedField<string>
    ) => {
        const newCancellationType = event.target.value as string;
        cancellationType.reset(newCancellationType);
        if (newCancellationType === 'EXTRAORDINARY_CANCELLATION') {
            cancellationDateType?.reset('ABSOLUTE_CANCELLATION_DATE');
        }
    };

    public copyMemberDataToFields = (targetFields: {
        firstname: WrappedField<string>;
        lastname: WrappedField<string>;
        dateOfBirth: WrappedField<LocalDate>;
        customerNumber: WrappedField<string | undefined>;
    }) => {
        targetFields.firstname.reset(
            this.memberDataForm.wrappedField.firstname.value
        );
        targetFields.lastname.reset(
            this.memberDataForm.wrappedField.lastname.value
        );
        targetFields.dateOfBirth.reset(
            this.memberDataForm.wrappedField.dateOfBirth.value
        );
        targetFields.customerNumber.reset(
            this.memberDataForm.wrappedField.customerNumber.value
        );
    };

    public get extraordinaryCancellationHelperText(): string {
        const { translate } = injectTSDI(I18n);
        return featureEnabled(
            'EXTRAORDINARY_CANCELLATION_ALTERNATIVE_HELPER_TEXT'
        )
            ? translate(
                  'public.content.terminate.contract.type.helper.alternative'
              )
            : translate('public.content.terminate.contract.type.helper');
    }

    public get manualExtraordinaryCancellationHelperText(): string {
        const { translate } = injectTSDI(I18n);
        return featureEnabled(
            'EXTRAORDINARY_CANCELLATION_ALTERNATIVE_HELPER_TEXT'
        )
            ? translate(
                  'public.content.terminate.contract.type.helper.manual.alternative'
              )
            : translate('public.content.terminate.contract.type.helper.manual');
    }
}
